"use client";

import { useState, useRef, useEffect } from "react";
import { useTranslations } from "next-intl";
import { useCurrency } from "@/context/CurrencyContext";
import { cn } from "@/lib/utils";
import { trackEvent } from "@/lib/track";

const COMMON_CURRENCIES = [
  { code: "USD", symbol: "$", name: "US Dollar" },
  { code: "EUR", symbol: "€", name: "Euro" },
  { code: "GBP", symbol: "£", name: "British Pound" },
  { code: "ILS", symbol: "₪", name: "Israeli Shekel" },
  { code: "CAD", symbol: "CA$", name: "Canadian Dollar" },
  { code: "AUD", symbol: "A$", name: "Australian Dollar" },
];

export default function CurrencySwitcher() {
  const { currencyCode, setCurrency } = useCurrency();
  const t = useTranslations("currency");
  const [isOpen, setIsOpen] = useState(false);
  const [dropDirection, setDropDirection] = useState<"down" | "up">("down");
  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        ref={buttonRef}
        onClick={() => {
          if (!isOpen && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            setDropDirection(spaceBelow < 280 ? "up" : "down");
          }
          setIsOpen(!isOpen);
        }}
        className="flex items-center gap-1 p-1.5 text-brand-dark/70 hover:text-brand-gold transition-colors"
        aria-label={t("selectCurrency")}
      >
        <span className="text-xs font-medium tracking-wider">
          {currencyCode}
        </span>
      </button>

      {isOpen && (
        <div
          className={cn(
            "absolute right-0 z-50 w-52 bg-white border border-gray-100 rounded-lg shadow-xl overflow-y-auto overscroll-contain max-h-[70vh]",
            dropDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"
          )}
        >
          {COMMON_CURRENCIES.map((cur) => (
            <button
              key={cur.code}
              onClick={() => {
                trackEvent("currency_switched", { from: currencyCode, to: cur.code });
                setCurrency(cur.code);
                setIsOpen(false);
              }}
              className={cn(
                "flex items-center justify-between w-full px-4 py-2.5 text-sm hover:bg-brand-cream/50 transition-colors",
                cur.code === currencyCode
                  ? "text-brand-gold font-medium bg-brand-cream/30"
                  : "text-brand-dark/70"
              )}
            >
              <span>
                {cur.symbol} {cur.name}
              </span>
              <span className="text-xs text-brand-muted">{cur.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
